/**
 * @file ContactModal.jsx
 * @description Public "Reach out" contact form for unauthenticated visitors.
 *   Posts to the no-auth `public-contact` edge function. Includes a honeypot
 *   field and Cloudflare Turnstile (when a site key is provisioned). When no
 *   site key is set, the form gracefully degrades to a mailto fallback so we
 *   never collect submissions we can't protect from spam.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module components/ContactModal
 */
import { useEffect, useRef, useState } from "react";
import { T } from "../theme";
import { Btn } from "./UI";
import { XIcon, MailIcon, CheckCircleIcon } from "./Icons";
import { supabase } from "../lib/supabase";
import { functionReply } from "../lib/functionErrorMessage";
import { validateEmail } from "../utils/validate";
import { useTranslation } from "react-i18next";
import { useModalViewport } from "../hooks/useModalViewport";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SUPPORT_EMAIL = "support@abacases.com";
const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

const TOPICS = ["General", "Partnership", "Press", "Support", "Other"];

// Loads the Turnstile script once. Guards against double-injection by tagging
// the script element and resolving immediately if the API is already present.
function loadTurnstileScript() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.turnstile) return resolve(true);
    const existing = document.querySelector(`script[data-turnstile="1"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.defer = true;
    s.setAttribute("data-turnstile", "1");
    s.addEventListener("load", () => resolve(true));
    s.addEventListener("error", () => resolve(false));
    document.head.appendChild(s);
  });
}

export default function ContactModal({ onClose, initialTopic = "General", initialMessage = "" }) {
  const vp = useModalViewport();
  const { t } = useTranslation();
  const [name, setName]       = useState("");
  const [email, setEmail]     = useState("");
  const [topic, setTopic]     = useState(initialTopic);
  const [message, setMessage] = useState(initialMessage);
  const [company, setCompany] = useState(""); // honeypot — humans leave this empty

  const [token, setToken]         = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent]           = useState(false);
  const [formError, setFormError] = useState("");           // top-level message
  const [fieldErrors, setFieldErrors] = useState({});       // { name?, email?, message? }

  const turnstileRef = useRef(null);
  const widgetIdRef  = useRef(null);

  // Render the Turnstile widget (only when a site key is configured).
  useEffect(() => {
    if (!SITE_KEY) return;
    let cancelled = false;
    loadTurnstileScript().then((ok) => {
      if (cancelled || !ok || !window.turnstile || !turnstileRef.current) return;
      // Avoid re-rendering into the same node
      if (widgetIdRef.current != null) return;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: SITE_KEY,
        callback: (tok) => setToken(tok),
        "error-callback": () => setToken(""),
        "expired-callback": () => setToken(""),
      });
    });
    return () => { cancelled = true; };
  }, []);

  function resetTurnstile() {
    setToken("");
    try {
      if (SITE_KEY && window.turnstile) window.turnstile.reset(widgetIdRef.current ?? undefined);
    } catch { /* widget may be gone — ignore */ }
  }

  function validateLocally() {
    const errs = {};
    if (!name.trim())    errs.name = t("contactModal.errName");
    if (validateEmail(email).valid !== true) errs.email = t("contactModal.errEmail");
    if (!message.trim()) errs.message = t("contactModal.errMessage");
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setFormError("");
    if (!validateLocally()) return;

    setSubmitting(true);
    const { data, error } = await supabase.functions.invoke("public-contact", {
      body: {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        topic,
        company, // honeypot — sent for everyone, empty for humans
        turnstileToken: token,
      },
    });

    // The function returns client-actionable failures as non-2xx, so supabase-js
    // surfaces them via `error` (FunctionsHttpError) with `data` null. functionReply
    // reads the reason from either shape.
    const reply = await functionReply({ data, error });
    const ok = !error && reply.body?.ok === true;
    const reason = ok ? null : reply.reason || null;

    if (ok) {
      setSent(true);
      setSubmitting(false);
      return;
    }

    // Map reason → user message
    switch (reason) {
      case "rate_limited":
        setFormError(t("contactModal.errRateLimited"));
        break;
      case "captcha_failed":
      case "captcha_missing":
        setFormError(t("contactModal.errCaptcha"));
        break;
      case "captcha_not_configured":
        setFormError(t("contactModal.errNotConfigured", { email: SUPPORT_EMAIL }));
        break;
      case "invalid_email":
        setFieldErrors((p) => ({ ...p, email: t("contactModal.errEmail") }));
        break;
      case "invalid_name":
        setFieldErrors((p) => ({ ...p, name: t("contactModal.errName") }));
        break;
      case "invalid_message":
        setFieldErrors((p) => ({ ...p, message: t("contactModal.errMessage") }));
        break;
      default:
        setFormError(t("contactModal.errGeneric", { email: SUPPORT_EMAIL }));
    }

    resetTurnstile();
    setSubmitting(false);
  }

  // ── Field styling helpers ───────────────────────────────────────────────────
  const labelStyle = { fontSize: 12, fontWeight: 600, color: T.textMid, marginBottom: 6, display: "block" };
  const fieldStyle = (hasErr) => ({
    width: "100%",
    padding: "10px 12px",
    border: `1.5px solid ${hasErr ? T.red : T.border}`,
    borderRadius: T.radiusMd,
    fontSize: 13,
    color: T.text,
    background: hasErr ? "#fff5f5" : "#fff",
    fontFamily: T.fontBody,
    outline: "none",
    boxSizing: "border-box",
  });
  const errTextStyle = { fontSize: 11, color: T.red, marginTop: 4 };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(10, 8, 30, 0.78)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px 16px",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480,
          background: T.surface,
          borderRadius: 16,
          border: `1px solid ${T.border}`,
          boxShadow: "0 24px 80px rgba(0,0,0,0.36)",
          display: "flex", flexDirection: "column",
          maxHeight: vp.maxH(1, 40),
          overflow: "hidden",
          fontFamily: T.fontBody,
        }}
      >
        {/* Header */}
        <div style={{
          padding: "18px 22px 14px",
          borderBottom: `1px solid ${T.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 9, flexShrink: 0,
              background: T.blueLight,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <MailIcon size={17} color={T.blue} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: T.text, letterSpacing: "-0.02em" }}>
                {t("contactModal.title")}
              </div>
              <div style={{ fontSize: 12, color: T.textLight, marginTop: 1 }}>
                {t("contactModal.subtitle")}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label={t("contactModal.close")}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: 6, display: "flex", alignItems: "center", color: T.textLight,
            }}
          >
            <XIcon size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 22px", overflowY: "auto" }}>
          {sent ? (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              textAlign: "center", padding: "20px 8px 12px",
            }}>
              <div style={{
                width: 56, height: 56, borderRadius: "50%",
                background: T.greenLight,
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 16,
              }}>
                <CheckCircleIcon size={28} color={T.green} />
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: T.text, marginBottom: 8 }}>
                {t("contactModal.sentTitle")}
              </div>
              <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, maxWidth: 320 }}>
                {t("contactModal.sentBodyPre")} <strong style={{ color: T.text }}>{email.trim()}</strong>.
              </div>
              <Btn onClick={onClose} style={{ marginTop: 22, padding: "9px 24px", fontSize: 13 }}>
                {t("contactModal.done")}
              </Btn>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {/* Name */}
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="contact-name" style={labelStyle}>{t("contactModal.nameLabel")}</label>
                <input
                  id="contact-name"
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setFieldErrors((p) => ({ ...p, name: undefined })); }}
                  placeholder={t("contactModal.namePlaceholder")}
                  autoComplete="name"
                  style={fieldStyle(!!fieldErrors.name)}
                />
                {fieldErrors.name && <div style={errTextStyle}>{fieldErrors.name}</div>}
              </div>

              {/* Email */}
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="contact-email" style={labelStyle}>{t("contactModal.emailLabel")}</label>
                <input
                  id="contact-email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setFieldErrors((p) => ({ ...p, email: undefined })); }}
                  onBlur={() => setFieldErrors((p) => ({
                    ...p,
                    email: email.trim() && validateEmail(email).valid !== true
                      ? t("contactModal.errEmail")
                      : p.email,
                  }))}
                  placeholder={t("contactModal.emailPlaceholder")}
                  autoComplete="email"
                  style={fieldStyle(!!fieldErrors.email)}
                />
                {fieldErrors.email && <div style={errTextStyle}>{fieldErrors.email}</div>}
              </div>

              {/* Topic */}
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="contact-topic" style={labelStyle}>{t("contactModal.topicLabel")}</label>
                <select
                  id="contact-topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  style={{
                    ...fieldStyle(false),
                    cursor: "pointer",
                    appearance: "none",
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2364748b' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 12px center",
                    paddingRight: 36,
                  }}
                >
                  {TOPICS.map((tp) => <option key={tp} value={tp}>{t(`contactModal.topic${tp}`)}</option>)}
                </select>
              </div>

              {/* Message */}
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="contact-message" style={labelStyle}>{t("contactModal.messageLabel")}</label>
                <textarea
                  id="contact-message"
                  value={message}
                  onChange={(e) => { setMessage(e.target.value); setFieldErrors((p) => ({ ...p, message: undefined })); }}
                  placeholder={t("contactModal.messagePlaceholder")}
                  rows={5}
                  maxLength={4000}
                  style={{ ...fieldStyle(!!fieldErrors.message), resize: "vertical", lineHeight: 1.5 }}
                />
                {fieldErrors.message && <div style={errTextStyle}>{fieldErrors.message}</div>}
              </div>

              {/* Honeypot — visually hidden, never shown to humans */}
              <input
                type="text"
                name="company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                tabIndex={-1}
                aria-hidden="true"
                autoComplete="off"
                style={{
                  position: "absolute", left: "-9999px", top: "auto",
                  width: 1, height: 1, overflow: "hidden", opacity: 0,
                }}
              />

              {/* Top-level error */}
              {formError && (
                <div style={{
                  marginBottom: 14, padding: "9px 12px",
                  background: `${T.red}12`, border: `1px solid ${T.red}30`,
                  borderRadius: T.radiusMd, fontSize: 12.5, color: T.red, lineHeight: 1.5,
                }}>
                  {formError}
                </div>
              )}

              {SITE_KEY ? (
                <>
                  {/* Turnstile widget */}
                  <div ref={turnstileRef} style={{ marginBottom: 14, minHeight: 65 }} />
                  <Btn
                    type="submit"
                    disabled={submitting || !token}
                    style={{
                      width: "100%", justifyContent: "center", padding: "11px 0", fontSize: 14,
                      opacity: (submitting || !token) ? 0.5 : 1,
                    }}
                  >
                    {submitting ? t("contactModal.submitting") : t("contactModal.submit")}
                  </Btn>
                </>
              ) : (
                /* No site key provisioned yet — graceful mailto fallback */
                <div style={{
                  padding: "14px 16px",
                  background: T.surfaceAlt,
                  border: `1px solid ${T.border}`,
                  borderRadius: T.radiusMd,
                  fontSize: 13, color: T.textMid, lineHeight: 1.6,
                }}>
                  {t("contactModal.fallbackNoticePre")}{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: T.blue, fontWeight: 600 }}>
                    {SUPPORT_EMAIL}
                  </a>.
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
